import axiosInstance from '../helpers/axios'
import { RestaurantAndPredictedRestaurant } from '../types'

/**
 * Group Dining Sessions — Phase 1 "Calendly for dinner" (see
 * resactly-dis/docs/GROUP_VIRAL_SESSIONS_PLAN.md).
 *
 * All calls go through the resactly-service proxy (REACT_APP_SERVICE_URL, via the shared axios
 * instance) which forwards to resactly-dis at the SAME paths — the browser never talks to dis
 * directly. The `/groups/public/session/**` endpoints are token-scoped and anon-friendly (no JWT):
 * the share token (path) + the device anon-id (body) authorize the call. `/api/group-session/claim`
 * is the one authenticated endpoint (merges the device's guest eater into the signed-in eater).
 */

/** Public view of a session (PII-free: only an aggregate member count). Mirrors dis GroupSessionResponse. */
export interface GroupSession {
  valid: boolean
  expired: boolean
  shareToken?: string
  sessionId?: string
  /** Returned only on create — the owner's participant token (their anon id). */
  ownerParticipantToken?: string
  name?: string
  location?: string
  /** Client-geocoded coordinates for `location` (null for older/ungeocoded sessions). */
  latitude?: number
  longitude?: number
  searchTerm?: string
  origin?: string
  memberCount: number
  partyCap: number
  /** True when the resolving caller owns this session. */
  isOwner?: boolean
  /**
   * True when the (authenticated) caller may save this session as a persistent group — owner, or an
   * active member adopting an orphan (phantom-owner) session. Gates the "Save this group" CTA.
   */
  canPersist?: boolean
  expiresAt?: string
  message?: string
}

/** Result of anonymously joining a session. Mirrors dis JoinGroupSessionResponse. */
export interface JoinGroupSessionResult {
  joined: boolean
  participantToken?: string
  sessionId?: string
  memberCount: number
  message?: string
}

/** Group recommendations payload (reuses the group-predict pipeline). Mirrors PredictedRestaurantResponse. */
export interface GroupSessionRecommendations {
  count: number
  page?: number
  totalCount?: number
  result: RestaurantAndPredictedRestaurant[]
  recommendationId?: string
  /**
   * True when the session location has no local restaurants yet — the backend has kicked off a
   * coverage fill (ingest + menu load) and results populate on later polls. Drives the
   * "expanding coverage" banner.
   */
  broadened?: boolean
  /**
   * True when the group has dietary needs but no restaurant can satisfy all of them (everyone must
   * be able to eat). Results are empty; drives the "no places match everyone's dietary needs"
   * banner.
   */
  dietaryUnmatched?: boolean
  /**
   * True when the session has a cuisine focus (searchTerm) but no restaurant nearby matches it.
   * Results are empty; drives the "no {cuisine} restaurants nearby" banner.
   */
  cuisineUnmatched?: boolean
}

/** Result of claiming/merging a guest eater into a real eater on signup. Mirrors ClaimGroupSessionResponse. */
export interface ClaimGroupSessionResult {
  claimed: boolean
  mergedMemberships: number
  mergedOwnedSessions: number
  message?: string
}

/** Result of graduating a dining session into a persistent social group. Mirrors PersistGroupSessionResponse. */
export interface PersistGroupSessionResult {
  persisted: boolean
  groupId?: string
  name?: string
  message?: string
}

export interface CreateGroupSessionParams {
  anonId: string
  name?: string
  location?: string
  /** Client-geocoded coordinates for `location`; enables the geo-radius candidate search. */
  latitude?: number
  longitude?: number
  searchTerm?: string
  cuisineLikes?: string
  dietaryPreferences?: string
  origin?: string
}

const invalidSession = (message?: string): GroupSession => ({
  valid: false,
  expired: false,
  memberCount: 0,
  partyCap: 0,
  message,
})

/**
 * Anonymously create a session. Returns the share token, session id, and the owner participant
 * token so the creator can act on the session.
 */
export const createGroupSession = async (
  params: CreateGroupSessionParams,
): Promise<GroupSession> => {
  const response = await axiosInstance.post<GroupSession>(
    '/groups/public/session',
    { origin: 'web', ...params },
  )
  return response.data
}

/**
 * "My dinners" for this anon device — the live sessions this anonId belongs to (owner or member),
 * so a user can return to sessions without the share link. Anon-scoped (keyed by the caller's own
 * anonId). Never throws — resolves to an empty list on error.
 */
export const fetchMySessions = async (
  anonId: string,
): Promise<GroupSession[]> => {
  try {
    const response = await axiosInstance.get<GroupSession[]>(
      `/groups/public/sessions?anonId=${encodeURIComponent(anonId)}`,
    )
    return Array.isArray(response.data) ? response.data : []
  } catch {
    return []
  }
}

/**
 * "My dinners" for a signed-in user — the live sessions the JWT eater belongs to, keyed by their
 * eater identity (authenticated). This is the signed-in path to rediscover a live session after
 * claim-on-signup nulls the device anon-id (which empties the anon-keyed `fetchMySessions`). Never
 * throws — resolves to an empty list on error (incl. when logged out / 401).
 */
export const fetchMyLiveSessions = async (): Promise<GroupSession[]> => {
  try {
    const response = await axiosInstance.get<GroupSession[]>(
      '/api/group-session/mine',
    )
    return Array.isArray(response.data) ? response.data : []
  } catch {
    return []
  }
}

/**
 * Resolve a session by share token (no auth wall). Invalid/expired tokens resolve to a graceful
 * `{ valid: false }` body (the backend returns 404 with that body — we surface it, never throw).
 */
export const resolveGroupSession = async (
  shareToken: string,
  anonId?: string,
): Promise<GroupSession> => {
  try {
    const response = await axiosInstance.get<GroupSession>(
      `/groups/public/session/${encodeURIComponent(shareToken)}`,
      { params: anonId ? { anonId } : undefined },
    )
    return response.data
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'valid' in body) {
      return body as GroupSession
    }
    return invalidSession('This dinner link is invalid or has expired.')
  }
}

/**
 * Anonymously join a session by share token (idempotent per anon-id). Party-cap / not-found
 * responses resolve to a `{ joined: false }` body rather than throwing.
 */
export const joinGroupSession = async (
  shareToken: string,
  anonId: string,
): Promise<JoinGroupSessionResult> => {
  try {
    const response = await axiosInstance.post<JoinGroupSessionResult>(
      `/groups/public/session/${encodeURIComponent(shareToken)}/join`,
      { anonId },
    )
    return response.data
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'joined' in body) {
      return body as JoinGroupSessionResult
    }
    return {
      joined: false,
      memberCount: 0,
      message: 'Could not join this dinner.',
    }
  }
}

/** Submit the 3-tap taste input (cuisines / vibe / dietary) for a participant's guest eater. */
export const submitSessionTaste = async (params: {
  shareToken: string
  anonId: string
  cuisines?: string
  vibe?: string
  dietary?: string
}): Promise<GroupSession> => {
  const { shareToken, anonId, cuisines, vibe, dietary } = params
  const response = await axiosInstance.post<GroupSession>(
    `/groups/public/session/${encodeURIComponent(shareToken)}/taste`,
    { anonId, cuisines, vibe, dietary },
  )
  return response.data
}

/**
 * Owner-only in-session edit of what the session searches for — its location (+ client-geocoded
 * lat/lng) and cuisine focus. Steers the whole party's candidate restaurant set mid-session; the
 * backend evicts the group caches so the next recommendations fetch recomputes. A non-owner gets a
 * 403 → resolved to `{ valid: false }` rather than throwing.
 */
export const updateSessionSearch = async (params: {
  shareToken: string
  anonId: string
  location?: string
  latitude?: number
  longitude?: number
  searchTerm?: string
}): Promise<GroupSession> => {
  const { shareToken, anonId, location, latitude, longitude, searchTerm } =
    params
  try {
    const response = await axiosInstance.patch<GroupSession>(
      `/groups/public/session/${encodeURIComponent(shareToken)}/search`,
      { anonId, location, latitude, longitude, searchTerm },
    )
    return response.data
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'valid' in body) {
      return body as GroupSession
    }
    return invalidSession('Could not update this dinner.')
  }
}

/**
 * Owner-only: delete (soft-delete) the session. After this it reads as not-found everywhere and
 * drops out of "my dinners". Returns true on success; a non-owner (403) or unknown session resolves
 * to false rather than throwing.
 */
export const deleteGroupSession = async (
  shareToken: string,
  anonId: string,
): Promise<boolean> => {
  try {
    const response = await axiosInstance.delete(
      `/groups/public/session/${encodeURIComponent(shareToken)}`,
      { params: { anonId } },
    )
    return response.data?.valid === true
  } catch {
    return false
  }
}

/** Group recommendations for the session (aggregated across everyone who's contributed). */
export const fetchGroupSessionRecommendations = async (
  shareToken: string,
  page = 0,
): Promise<GroupSessionRecommendations> => {
  const response = await axiosInstance.get<GroupSessionRecommendations>(
    `/groups/public/session/${encodeURIComponent(shareToken)}/recommendations`,
    { params: { page } },
  )
  return response.data
}

/**
 * Phase 3 — RSVP + voting + "the pick". These endpoints are token-scoped and anon-friendly (no JWT):
 * the share token (path) + the device anon-id (body) authorize the call, same as join/taste.
 */

/** An attendance response for a session participant. Mirrors dis RsvpStatus. */
export type RsvpStatus = 'GOING' | 'MAYBE' | 'DECLINED'

/** Aggregate RSVP breakdown across everyone in the session. Mirrors dis RsvpSummary. */
export interface RsvpSummary {
  going: number
  maybe: number
  declined: number
  noResponse: number
}

/** Result of submitting/updating an RSVP. Mirrors dis RsvpResponse. */
export interface SessionRsvpResult {
  valid: boolean
  yourRsvp?: RsvpStatus
  rsvpSummary?: RsvpSummary
  memberCount: number
}

/** Result of casting/updating a vote for a restaurant. Mirrors dis VoteResponse. */
export interface SessionVoteResult {
  valid: boolean
  restaurantAlias?: string
}

/** Per-restaurant vote count in the tally. Mirrors dis RestaurantTally. */
export interface RestaurantTally {
  restaurantAlias: string
  votes: number
}

/** Live vote tally + the winning pick for a session. Mirrors dis TallyResponse. */
export interface SessionTally {
  valid: boolean
  totalMembers: number
  totalVotes: number
  tallies: RestaurantTally[]
  /** The leading restaurant alias — the group's pick. Absent until at least one vote is cast. */
  winner?: string
  rsvpSummary?: RsvpSummary
  /** The caller's own current vote (restaurant alias), for on-load button hydration. */
  yourVote?: string
  /** The caller's own current RSVP, for on-load button hydration. */
  yourRsvp?: RsvpStatus
}

/**
 * Submit (or update) the device's RSVP for a session. Not-found / invalid responses resolve to a
 * `{ valid: false }` body rather than throwing so the UI can degrade gracefully.
 */
export const submitRsvp = async (
  shareToken: string,
  anonId: string,
  status: RsvpStatus,
): Promise<SessionRsvpResult> => {
  try {
    const response = await axiosInstance.post<SessionRsvpResult>(
      `/groups/public/session/${encodeURIComponent(shareToken)}/rsvp`,
      { anonId, status },
    )
    return response.data
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'valid' in body) {
      return body as SessionRsvpResult
    }
    return { valid: false, memberCount: 0 }
  }
}

/**
 * Cast (or change) the device's vote for a restaurant in the session. Invalid responses resolve to
 * a `{ valid: false }` body rather than throwing.
 */
export const castVote = async (
  shareToken: string,
  anonId: string,
  restaurantAlias: string,
): Promise<SessionVoteResult> => {
  try {
    const response = await axiosInstance.post<SessionVoteResult>(
      `/groups/public/session/${encodeURIComponent(shareToken)}/vote`,
      { anonId, restaurantAlias },
    )
    return response.data
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'valid' in body) {
      return body as SessionVoteResult
    }
    return { valid: false }
  }
}

/**
 * Fetch the live vote tally (per-restaurant counts + the winning pick) for a session. Invalid /
 * not-found responses resolve to a `{ valid: false }` body rather than throwing.
 */
export const fetchSessionTally = async (
  shareToken: string,
  anonId?: string,
): Promise<SessionTally> => {
  try {
    const response = await axiosInstance.get<SessionTally>(
      `/groups/public/session/${encodeURIComponent(shareToken)}/tally`,
      { params: anonId ? { anonId } : undefined },
    )
    return response.data
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'valid' in body) {
      return body as SessionTally
    }
    return { valid: false, totalMembers: 0, totalVotes: 0, tallies: [] }
  }
}

/**
 * Planner picks — the restaurants members explicitly ADDED to a planner (the shortlist the group
 * votes on), as opposed to the geo/taste-derived candidates the recommender computes. Mirrors dis
 * SessionCandidateResponse.
 */
export interface SessionCandidate {
  restaurantAlias: string
  restaurantId?: string
  name?: string
  city?: string
  cuisine?: string
  sourceSurface?: string
}

/** Result of any add/remove/list call on a planner's shortlist. Mirrors dis SessionCandidateResponse. */
export interface SessionCandidatesResult {
  valid: boolean
  sessionId?: string
  candidates: SessionCandidate[]
  candidateCount: number
  message?: string
}

const emptyCandidates = (message?: string): SessionCandidatesResult => ({
  valid: false,
  candidates: [],
  candidateCount: 0,
  message,
})

/** Normalize a candidates payload — the backend always returns the full list, never a delta. */
const toCandidatesResult = (data: unknown): SessionCandidatesResult => {
  const body = (data ?? {}) as Partial<SessionCandidatesResult>
  return {
    valid: body.valid === true,
    sessionId: body.sessionId,
    candidates: Array.isArray(body.candidates) ? body.candidates : [],
    candidateCount:
      typeof body.candidateCount === 'number'
        ? body.candidateCount
        : (body.candidates?.length ?? 0),
    message: body.message,
  }
}

/**
 * Add a restaurant to a planner's shortlist. Idempotent server-side — a repeat tap on the same
 * restaurant returns the unchanged shortlist rather than duplicating the pick, so a double-tap (or
 * a retried request on a flaky mobile link) can never create two entries. Never throws.
 */
export const addSessionCandidate = async (params: {
  shareToken: string
  anonId: string
  restaurantAlias: string
  sourceSurface?: string
}): Promise<SessionCandidatesResult> => {
  const { shareToken, anonId, restaurantAlias, sourceSurface } = params
  try {
    const response = await axiosInstance.post(
      `/groups/public/session/${encodeURIComponent(shareToken)}/candidates`,
      { anonId, restaurantAlias, sourceSurface },
    )
    return toCandidatesResult(response.data)
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'valid' in body) {
      return toCandidatesResult(body)
    }
    return emptyCandidates('Could not add this restaurant to your planner.')
  }
}

/** Remove a restaurant from a planner's shortlist. Idempotent; never throws. */
export const removeSessionCandidate = async (params: {
  shareToken: string
  anonId: string
  restaurantAlias: string
}): Promise<SessionCandidatesResult> => {
  const { shareToken, anonId, restaurantAlias } = params
  try {
    const response = await axiosInstance.delete(
      `/groups/public/session/${encodeURIComponent(
        shareToken,
      )}/candidates/${encodeURIComponent(restaurantAlias)}`,
      { params: { anonId } },
    )
    return toCandidatesResult(response.data)
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'valid' in body) {
      return toCandidatesResult(body)
    }
    return emptyCandidates('Could not remove this restaurant from your planner.')
  }
}

/**
 * The planner's shortlist. Public/read-only (the share token is the capability) so an invitee sees
 * the picks before they join. Never throws — resolves to an empty list on error.
 */
export const fetchSessionCandidates = async (
  shareToken: string,
): Promise<SessionCandidatesResult> => {
  try {
    const response = await axiosInstance.get(
      `/groups/public/session/${encodeURIComponent(shareToken)}/candidates`,
    )
    return toCandidatesResult(response.data)
  } catch {
    return emptyCandidates()
  }
}

/**
 * Claim/merge on signup (authenticated): merges the device's guest eater — taste + session
 * memberships — into the JWT eater. Idempotent; mirrors claimAnonReviews / claimAnonPoints.
 * `shareToken` is accepted for call-site symmetry but the backend keys the merge on the anon id.
 */
export const claimGroupSession = async (
  anonId: string,
  _shareToken?: string,
): Promise<ClaimGroupSessionResult> => {
  const response = await axiosInstance.post<ClaimGroupSessionResult>(
    '/api/group-session/claim',
    { anonId },
  )
  return response.data
}

/**
 * Graduation bridge (authenticated, owner-only): save the dining session as a persistent social
 * group. Drops the session TTL server-side so it stops being ephemeral and starts appearing in the
 * owner's persistent groups list. A non-owner gets a 403 → resolved to `{ persisted: false }`.
 */
export const persistGroupSession = async (
  shareToken: string,
  name?: string,
): Promise<PersistGroupSessionResult> => {
  try {
    const response = await axiosInstance.post<PersistGroupSessionResult>(
      '/api/group-session/persist',
      { shareToken, name },
    )
    return response.data
  } catch (error: any) {
    const body = error?.response?.data
    if (body && typeof body === 'object' && 'persisted' in body) {
      return body as PersistGroupSessionResult
    }
    return {
      persisted: false,
      message: 'Could not save this group. Please try again.',
    }
  }
}
